import { Datatable } from './Datatable';
import {
  initInactiveScannerTable,
  initInactiveScannerConfig,
  initInactiveScannerOrders,
} from './js/portal/inactive_scanner';

const tableId = 'table-inactive-scanner-results';
const ordersTableId = 'table-inactive-scanner-orders';

export default function AdminInactiveScannerPage() {
  const page = (
    <>
      <div class="row">
        <div class="col-lg-12">
          <div class="card form-card">
            <div class="card-header">Inactive Resource Management</div>
            <div class="card-body">
              <ul class="nav nav-tabs" role="tablist">
                <li class="nav-item">
                  <a
                    class="nav-link active"
                    data-bs-target="#inactive-scanner-results-tab"
                    role="tab"
                    data-bs-toggle="tab"
                    aria-selected="true">
                    <i class="fas fa-list me-1"></i> Scan Results
                  </a>
                </li>
                <li class="nav-item">
                  <a
                    class="nav-link"
                    id="inactive-scanner-config-tab-link"
                    data-bs-target="#inactive-scanner-config-tab"
                    role="tab"
                    data-bs-toggle="tab"
                    aria-selected="false">
                    <i class="fas fa-cog me-1"></i> Configuration
                  </a>
                </li>
                <li class="nav-item">
                  <a
                    class="nav-link"
                    id="inactive-scanner-orders-tab-link"
                    data-bs-target="#inactive-scanner-orders-tab"
                    role="tab"
                    data-bs-toggle="tab"
                    aria-selected="false">
                    <i class="fas fa-clipboard-list me-1"></i> Related Orders
                  </a>
                </li>
              </ul>
              <div class="tab-content">
                {/* ── Scan Results Tab ── */}
                <div class="tab-pane fade active show" id="inactive-scanner-results-tab" role="tabpanel">
                  <div class="infobox card card-body mt-3">
                    <p>
                      <i class="fas fa-info-circle text-primary me-2"></i>
                      This page lists resources detected as inactive by the automated scanner. Resources are evaluated
                      against inactivity criteria and may be automatically cleaned up if no action is taken.
                    </p>
                    <p style="font-size: 1.1rem; color: #8B4513;">
                      <i class="fas fa-exclamation-triangle me-2" style="font-size: 1.4rem; color: #d4a017;"></i>
                      <strong>Export note:</strong> The CSV export reflects the current state of the tracking database,
                      which may be outdated if a scan is in progress or resources have changed since the last run.
                    </p>
                    <p class="mb-1">
                      <strong>Status legend:</strong>
                    </p>
                    <ul class="mb-0" style="list-style: none; padding-left: 0;">
                      <li class="mb-1">
                        <span class="badge bg-danger me-2">detected</span> Inactive, no order yet
                      </li>
                      <li class="mb-1">
                        <span class="badge bg-warning text-dark me-2">detected_dry_run</span> Inactive, orders disabled
                      </li>
                      <li class="mb-1">
                        <span class="badge bg-success me-2">order_created</span> Cleanup order submitted
                      </li>
                      <li class="mb-1">
                        <span class="badge bg-warning text-dark me-2">skipped_pending</span> Existing order in progress
                        (customer-submitted or auto-created)
                      </li>
                      <li class="mb-1">
                        <span class="badge bg-warning text-dark me-2">skipped_cooldown</span> Declined order cooldown
                      </li>
                      <li class="mb-1">
                        <span class="badge bg-secondary me-2">active</span> Previously inactive, now recovered
                      </li>
                    </ul>
                  </div>
                  {/* Stats bar */}
                  <div id="inactive-scanner-stats" class="mt-3" style="display:none;">
                    <div class="statsContainer">
                      <div class="col-12">
                        <div class="statsCard">
                          <div class="statsCardBody text-center row justify-content-center">
                            <div class="col-xs-6 col-sm-4 col-lg-2 pb-3">
                              <h5 class="scanner-stat-label">Total</h5>
                              <div class="statsContentValue scanner-stat-value" id="scanner-stat-total">
                                0
                              </div>
                            </div>
                            <div class="col-xs-6 col-sm-4 col-lg-2 pb-3">
                              <h5 class="scanner-stat-label">Inactive</h5>
                              <div class="statsContentValue scanner-stat-value" id="scanner-stat-inactive">
                                0
                              </div>
                            </div>
                            <div class="col-xs-6 col-sm-4 col-lg-2 pb-3">
                              <h5 class="scanner-stat-label">Accounts</h5>
                              <div class="statsContentValue scanner-stat-value" id="scanner-stat-accounts">
                                0
                              </div>
                            </div>
                            <div class="col-xs-6 col-sm-4 col-lg-2 pb-3">
                              <h5 class="scanner-stat-label">VPCs</h5>
                              <div class="statsContentValue scanner-stat-value" id="scanner-stat-vpcs">
                                0
                              </div>
                            </div>
                            <div class="col-xs-6 col-sm-4 col-lg-2 pb-3">
                              <h5 class="scanner-stat-label">Errors</h5>
                              <div class="statsContentValue scanner-stat-value danger" id="scanner-stat-errors">
                                0
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Datatable tableId={tableId} />
                </div>

                {/* ── Configuration Tab ── */}
                <div class="tab-pane fade" id="inactive-scanner-config-tab" role="tabpanel">
                  <div class="d-flex justify-content-start mt-3 mb-2">
                    <button type="button" class="btn btn-warning" id="btn-trigger-inactive-scan">
                      <i class="fas fa-play me-2"></i>Trigger Scan
                    </button>
                  </div>
                  <div class="alert alert-warning border-warning mt-3 mb-0" role="alert">
                    <div class="d-flex align-items-start mb-2">
                      <i class="fas fa-info-circle text-primary me-2 mt-1" style="font-size: 1.1rem;"></i>
                      <div>
                        View the base configuration (managed via Terraform) and edit the runtime overrides. Override
                        ignore-lists are <strong>merged</strong> (set union) with the base config. Override thresholds{' '}
                        <strong>replace</strong> the base values entirely when set.
                      </div>
                    </div>
                    <div class="d-flex align-items-start">
                      <i class="fas fa-exclamation-triangle text-warning me-2 mt-1" style="font-size: 1.1rem;"></i>
                      <div>
                        <strong>Caution:</strong> Modifying overrides (adding or removing ignore-list entries, changing
                        thresholds) will affect future scan evaluations and may alter the status of resources already
                        detected or tracked.
                      </div>
                    </div>
                  </div>

                  <div id="inactive-scanner-config-loading" class="text-center py-5" style="display:none;">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="text-muted mt-2">Loading configuration…</p>
                  </div>

                  <div id="inactive-scanner-config-content" style="display:none;">
                    {/* ── Base Config (read-only) ── */}
                    <div class="card mt-3 scanner-config-section">
                      <div
                        class="card-header scanner-config-header"
                        style="cursor: pointer;"
                        data-bs-toggle="collapse"
                        data-bs-target="#scanner-baseconfig-collapse"
                        aria-expanded="true"
                        aria-controls="scanner-baseconfig-collapse">
                        <div class="d-flex align-items-center">
                          <i
                            class="fas fa-chevron-down me-2 scanner-collapse-icon-base"
                            style="transition: transform 0.2s;"></i>
                          <i class="fas fa-lock me-2"></i>
                          <span class="scanner-config-title">Base Configuration</span>
                          <span class="badge bg-secondary scanner-config-badge ms-3">
                            Terraform-managed · read-only
                          </span>
                        </div>
                      </div>
                      <div class="collapse show" id="scanner-baseconfig-collapse">
                        <div class="card-body">
                          <div class="alert alert-info d-flex align-items-start mb-4" role="alert">
                            <i class="fas fa-info-circle me-2 mt-1"></i>
                            <div>
                              These values are managed via Terraform and cannot be edited here. Runtime overrides
                              configured below may extend lists (e.g. additional ignored accounts) or replace thresholds
                              entirely. Refer to the <strong>Runtime Overrides</strong> section for any active
                              modifications.
                            </div>
                          </div>
                          <div id="inactive-scanner-base-config"></div>
                        </div>
                      </div>
                    </div>

                    {/* ── Overrides (editable) ── */}
                    <div class="card mt-4 mb-3 scanner-config-section">
                      <div
                        class="card-header scanner-config-header"
                        style="cursor: pointer;"
                        data-bs-toggle="collapse"
                        data-bs-target="#scanner-overrides-collapse"
                        aria-expanded="true"
                        aria-controls="scanner-overrides-collapse">
                        <div class="d-flex align-items-center gap-3 flex-wrap">
                          <div class="d-flex align-items-center">
                            <i
                              class="fas fa-chevron-down me-2 text-primary scanner-collapse-icon"
                              style="transition: transform 0.2s;"></i>
                            <i class="fas fa-edit me-2 text-primary"></i>
                            <span class="scanner-config-title">Runtime Overrides</span>
                            <span class="badge bg-primary scanner-config-badge ms-3">Admin UI · editable</span>
                          </div>
                          <div>
                            <button
                              class="btn btn-primary"
                              id="scanner-config-save-btn"
                              title="Save overrides"
                              onclick="event.stopPropagation();">
                              <i class="fas fa-save me-1"></i> Save Overrides
                            </button>
                          </div>
                        </div>
                      </div>
                      <div class="collapse show" id="scanner-overrides-collapse">
                        <div class="card-body" id="inactive-scanner-overrides-form"></div>
                        <div class="card-footer text-end bg-light">
                          <button
                            class="btn btn-primary btn-lg"
                            id="scanner-config-save-btn-bottom"
                            title="Save overrides">
                            <i class="fas fa-save me-1"></i> Save Overrides
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Related Orders Tab ── */}
                <div class="tab-pane fade" id="inactive-scanner-orders-tab" role="tabpanel">
                  <div class="infobox card card-body mt-3">
                    <p>
                      <i class="fas fa-info-circle text-primary me-2"></i>
                      Orders created by the inactive resource scanner — auto-destroy-vpc and auto-close-account actions
                      only.
                    </p>
                  </div>
                  <Datatable tableId={ordersTableId} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  async function onPageReady() {
    initInactiveScannerTable(tableId);
    initInactiveScannerConfig();
    initInactiveScannerOrders(ordersTableId);
  }

  return [page, onPageReady];
}
